import {
  PROMPT_CATEGORY_IDS,
  PromptCategoryId,
  ProposedTactic,
} from "@shaarp/common";
import { UIMessage } from "ai";

/**
 * The `proposeTactic` tool returns the proposal, or a sentence when it refused
 * an empty one. Only the structured shape draws a card; every field is checked
 * because the guard is what tells TypeScript the value is one.
 */
export function isProposedTactic(output: unknown): output is ProposedTactic {
  if (!output || typeof output !== "object") {
    return false;
  }
  const candidate = output as Record<string, unknown>;
  return (
    typeof candidate.title === "string" &&
    typeof candidate.description === "string" &&
    typeof candidate.content === "string" &&
    PROMPT_CATEGORY_IDS.includes(candidate.categoryId as PromptCategoryId)
  );
}

/**
 * The `proposeTactic` calls of a thread that drew a review card, in thread
 * order: the ones a saved tactic may have come from.
 */
export function collectProposedTacticCallIds(
  messages: readonly UIMessage[],
): string[] {
  const ids: string[] = [];
  for (const message of messages) {
    for (const part of message.parts) {
      if (
        part.type === "tool-proposeTactic" &&
        "state" in part &&
        part.state === "output-available" &&
        isProposedTactic(part.output)
      ) {
        ids.push(part.toolCallId);
      }
    }
  }
  return [...new Set(ids)];
}

/**
 * The earlier calls a revised proposal replaces, latest first, as the tool
 * names them; empty for a first proposal or anything else.
 */
export function readRevisedToolCallIds(output: unknown): string[] {
  const ids = (output as { revisesToolCallIds?: unknown } | null)
    ?.revisesToolCallIds;
  return Array.isArray(ids)
    ? ids.filter((id): id is string => typeof id === "string")
    : [];
}

/**
 * The `proposeTactic` calls of a thread that a later proposal revised: their
 * cards show they were replaced instead of a second form to save.
 */
export function collectRevisedProposedTacticCallIds(
  messages: readonly UIMessage[],
): string[] {
  const ids = new Set<string>();
  for (const message of messages) {
    for (const part of message.parts) {
      if (
        part.type === "tool-proposeTactic" &&
        "state" in part &&
        part.state === "output-available"
      ) {
        for (const id of readRevisedToolCallIds(part.output)) {
          ids.add(id);
        }
      }
    }
  }
  return [...ids].sort();
}
